import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Zap, LineChart, Scale, Calculator, DollarSign } from 'lucide-react';
import { SILVER_ADVANTAGES } from '../mock';
import { useCountry, useCtaHandler } from '../context/CountryContext';
import FadeInSection from './FadeInSection';

const AdvIcon = ({ name }) => {
  const cls = 'w-5 h-5';
  if (name === 'zap') return <Zap className={cls} />;
  if (name === 'chart') return <LineChart className={cls} />;
  if (name === 'scale') return <Scale className={cls} />;
  return null;
};

export default function SilverAdvantage() {
  const [lots, setLots] = useState(0.5);
  const [pips, setPips] = useState(50);
  const { telegramUrl } = useCountry();
  const track = useCtaHandler();

  // XAGUSD Formula:
  // 1 pip = $0.01 movement, 1 std lot = 5,000 oz
  // Pip Value USD per lot = lot × 5000 × 0.01 = lot × 50
  const pipValuePerLot = lots * 50; // USD per 1 pip at chosen lot size
  const dailyProfit = Math.round(pipValuePerLot * pips); // simple daily profit

  const lotsPct = useMemo(() => ((lots - 0.01) / (10 - 0.01)) * 100, [lots]);
  const pipsPct = useMemo(() => ((pips - 10) / (250 - 10)) * 100, [pips]);

  return (
    <section className="relative py-24 md:py-32">
      <div className="absolute inset-x-0 top-20 h-[400px] radial-glow-soft opacity-60 pointer-events-none" />
      <div className="relative max-w-6xl mx-auto px-6">
        <FadeInSection className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-extrabold text-white text-4xl md:text-[52px] leading-[1.05] tracking-tight">
            The ultimate shortcut. <span className="text-orange-gradient">Trade Silver.</span>
          </h2>
          <p className="mt-5 text-[#f2f2f2] text-[15px]">
            Only Silver (XAGUSD) volume counts in this challenge. That is why smart traders lock in on one clean instrument, stack lots fast, and hit their milestones ahead of everyone else.
          </p>
        </FadeInSection>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column - Chart card + advantages */}
          <FadeInSection delay={0.1} className="space-y-5">
            <div className="card-dark overflow-hidden">
              <div className="relative h-56">
                <img
                  loading="lazy"
                  decoding="async"
                  src="https://images.unsplash.com/photo-1689732888407-310424e3a372?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxzaWx2ZXIlMjB0cmFkaW5nJTIwY2hhcnR8ZW58MHx8fHwxNzg0MjczMDcxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Silver Chart"
                  className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,10,11,0.1) 0%, rgba(10,10,11,0.9) 100%)' }} />
                <div className="absolute top-4 left-5 flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#f2f2f2]">XAG/USD</span>
                </div>
                <div className="absolute bottom-4 left-5">
                  <div className="font-display font-bold text-2xl text-white">Silver. High Velocity.</div>
                  <div className="text-[#f2f2f2] text-sm">Fast Lots</div>
                </div>
              </div>
            </div>

            {SILVER_ADVANTAGES.map((a) => (
              <div key={a.title} className="card-dark p-5 flex gap-4 items-start">
                <div className="icon-badge shrink-0"><AdvIcon name={a.icon} /></div>
                <div>
                  <div className="text-white font-semibold uppercase tracking-wide">{a.title}</div>
                  <p className="mt-1.5 text-[#f2f2f2] text-[13.5px] leading-relaxed">{a.desc}</p>
                </div>
              </div>
            ))}
          </FadeInSection>

          {/* Right column - Calculator */}
          <FadeInSection delay={0.15} className="card-dark p-6 md:p-8 h-fit">
            <div className="flex items-center gap-2 text-[11.5px] uppercase tracking-[0.18em] text-[#ff9b5a]">
              <Calculator className="w-4 h-4" /> Live Silver Profit Calculator
            </div>
            <h3 className="mt-3 font-display font-bold text-2xl md:text-3xl text-white">
              Estimate your daily Silver <span className="text-orange-gradient">profit</span>
            </h3>
            <p className="mt-2 text-[#f2f2f2] text-[13px]">XAGUSD: 1 lot = 5,000 oz. 1 pip ($0.01) = USD 50 per standard lot.</p>

            {/* Slider 1 */}
            <div className="mt-7">
              <div className="flex items-baseline justify-between">
                <div className="text-[#f2f2f2] text-[13px]">Silver lots per day</div>
                <div className="text-white font-semibold">{lots.toFixed(2)} Lots</div>
              </div>
              <input
                type="range"
                min={0.01}
                max={10}
                step={0.01}
                value={lots}
                onChange={(e) => setLots(parseFloat(e.target.value))}
                className="silver-range mt-3"
                style={{ ['--val']: `${lotsPct}%` }}
              />
              <div className="mt-2 flex justify-between text-[11px] text-[#f2f2f2]">
                <span>0.01</span><span>2.5</span><span>5.0</span><span>10.0</span>
              </div>
              <div className="mt-2 text-[11.5px] text-[#f2f2f2]">
                Pip value: <span className="text-[#f2f2f2] font-semibold">USD {pipValuePerLot.toFixed(2)}</span> per pip &middot; 1 std lot = 5,000 oz &middot; 1 pip = $0.01
              </div>
            </div>

            {/* Slider 2 */}
            <div className="mt-6">
              <div className="flex items-baseline justify-between">
                <div className="text-[#f2f2f2] text-[13px]">Average pips per Silver trade</div>
                <div className="text-white font-semibold">{pips} Pips</div>
              </div>
              <input
                type="range"
                min={10}
                max={250}
                step={5}
                value={pips}
                onChange={(e) => setPips(parseInt(e.target.value))}
                className="silver-range mt-3"
                style={{ ['--val']: `${pipsPct}%` }}
              />
              <div className="mt-2 flex justify-between text-[11px] text-[#f2f2f2]">
                <span>10</span><span>80</span><span>160</span><span>250</span>
              </div>
            </div>

            {/* Result - Simple daily Silver profit */}
            <div className="mt-8 rounded-2xl border border-white/10 p-5 bg-white/[0.02]">
              <div className="text-[11.5px] uppercase tracking-[0.18em] text-[#f2f2f2]">Estimated Daily Silver Profit</div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-[#f2f2f2] text-sm"><DollarSign className="w-4 h-4 text-[#ff9b5a]" /> Profit per trading day</div>
                <div className="font-display font-extrabold text-2xl text-orange-gradient">USD {dailyProfit.toLocaleString()}</div>
              </div>
              <div className="mt-2 text-[12px] text-[#f2f2f2]">
                Formula: {lots.toFixed(2)} lots × {pips} pips × USD 50 per pip per lot.
              </div>
            </div>

            <a href={telegramUrl} target="_blank" rel="noreferrer" onClick={() => track('calculator-cta')} className="btn-orange w-full mt-6 inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[14.5px]">
              Claim My Bali Ticket <ArrowUpRight className="w-4 h-4" />
            </a>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
}
